import { FEATURES } from '@/lib/Features'
import { ACCESS_LEVELS_DICT } from '@/lib/Access'
import { createRouter, createWebHistory } from 'vue-router'
import { AUTH_STATE } from '@/store/user.module'
import { boardNavigationService } from '@/services/board-navigation.service'
import AuthLayout from '@/layouts/AuthLayout.vue'
import VerificationLayout from '@/layouts/VerificationLayout.vue'
import AppLayout from '@/layouts/AppLayout.vue'
import { until } from '@vueuse/core'
import store from '@/store'
import { featureAccessService } from '@/services/auth/feature-access.service'
import { routeSpanManager } from '@/routeSpanManager'
import EmptyLayout from '@/layouts/EmptyLayout.vue'
import { getEmbedStatus, getEmbedTrustStatus } from '@/hooks/use-embed-mode'
import {
  saveSyncBoardId,
  checkSyncStatus,
  clearSyncBoardId
} from '@/hooks/use-dayforce-sync-status'
import { logger } from '@/utils/logger'

const baseUrl = '/'
const EMBED_ALLOWED_DESTINATIONS = {
  settings: ['Settings', 'API Keys Settings'],
  'access-control': ['InviteAccessUsers']
}

const AUTH_RULES = {
  AuthLayout: {
    allowedAuthStates: [AUTH_STATE.UNAUTHENTICATED],
    redirects: {
      [AUTH_STATE.UNVERIFIED]: 'SetupUser',
      [AUTH_STATE.VERIFIED]: 'Landing',
      [AUTH_STATE.NEEDS_MFA]: 'MFA'
    }
  },
  VerificationLayout: {
    allowedAuthStates: [AUTH_STATE.UNVERIFIED, AUTH_STATE.NEEDS_MFA, AUTH_STATE.VERIFIED],
    redirects: {
      [AUTH_STATE.UNAUTHENTICATED]: 'Home',
      [AUTH_STATE.VERIFIED]: 'Landing',
      [AUTH_STATE.NEEDS_MFA]: 'MFA'
    }
  },
  AppLayout: {
    allowedAuthStates: [AUTH_STATE.VERIFIED],
    redirects: {
      [AUTH_STATE.UNAUTHENTICATED]: 'Home',
      [AUTH_STATE.UNVERIFIED]: 'SetupUser',
      [AUTH_STATE.NEEDS_MFA]: 'MFA'
    }
  },
  EmbedLayout: {
    // Allow both authenticated and unauthenticated users
    // This supports both fresh authentication and already-authenticated navigation
    allowedAuthStates: [AUTH_STATE.UNAUTHENTICATED, AUTH_STATE.VERIFIED, AUTH_STATE.UNVERIFIED],
    redirects: {
      [AUTH_STATE.NEEDS_MFA]: 'MFA'
    }
  }
}

const routes = [
  {
    path: '/',
    name: 'Home',
    component: () => import('@/views/AuthView.vue'),
    meta: {
      layout: AuthLayout,
      embedLayout: EmptyLayout,
      authRules: AUTH_RULES.AuthLayout
    }
  },
  {
    path: '/super-admin',
    name: 'Super Admin',
    component: () => import('@/views/SuperAdminView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Super Admin',
      layout: AppLayout,
      authRules: AUTH_RULES.AppLayout
    }
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: () => import('@/views/ForgotPasswordView.vue'),
    meta: {
      layout: AuthLayout,
      embedLayout: EmptyLayout,
      authRules: AUTH_RULES.AuthLayout,
      requiresOobCode: true
    }
  },
  {
    path: '/signup',
    name: 'SignUp',
    component: () => import('@/views/SignUpView.vue'),
    meta: {
      layout: AuthLayout,
      embedLayout: EmptyLayout,
      authRules: AUTH_RULES.AuthLayout
    }
  },
  {
    path: '/setup-user',
    name: 'SetupUser',
    component: () => import('@/views/SetupUserView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Setup user',
      layout: VerificationLayout,
      authRules: AUTH_RULES.VerificationLayout
    }
  },
  {
    path: '/update-authentication',
    name: 'UpdateAuthentication',
    component: () => import('@/views/UpdateAuthentication.vue'),
    meta: {
      requiresAuth: true,
      title: 'Login Methods',
      layout: VerificationLayout,
      authRules: AUTH_RULES.VerificationLayout
    }
  },
  {
    path: '/login-redirect',
    name: 'LoginRedirect',
    component: () => import('@/views/LoginRedirect.vue'),
    meta: {
      layout: AuthLayout,
      embedLayout: EmptyLayout,
      authRules: AUTH_RULES.AuthLayout
    }
  },
  {
    path: '/embed',
    name: 'Embed',
    component: () => import('@/views/EmbedView.vue'),
    meta: {
      layout: EmptyLayout,
      authRules: AUTH_RULES.EmbedLayout
    }
  },
  {
    path: '/embed-no-boards',
    name: 'EmbedNoBoards',
    component: () => import('@/views/EmbedNoBoards.vue'),
    meta: {
      requiresAuth: true,
      title: 'Setup Workspace',
      layout: EmptyLayout,
      // Use embed rules so embedded sessions (often unverified) don't bounce to SetupUser
      authRules: AUTH_RULES.EmbedLayout
    }
  },
  {
    path: '/dayforce-onboarding/:boardId',
    name: 'DayforceOnboarding',
    component: () => import('@/views/DayforceOnboardingView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Dayforce Setup',
      layout: EmptyLayout,
      authRules: AUTH_RULES.EmbedLayout
    }
  },
  {
    path: '/mfa',
    name: 'MFA',
    component: () => import('@/views/MFA.vue'),
    meta: {
      requiresAuth: true,
      layout: VerificationLayout,
      authRules: AUTH_RULES.VerificationLayout
    }
  },
  {
    path: '/:boardId?',
    name: 'Landing',
    component: () => import('@/views/LandingPage.vue'),
    meta: {
      requiresAuth: true,
      layout: AppLayout,
      authRules: AUTH_RULES.AppLayout
    }
  },
  {
    path: '/hub/:boardId',
    name: 'Hub',
    component: () => import('@/views/hub/HubLayout.vue'),
    meta: {
      requiresAuth: true,
      title: 'Hub',
      feature: FEATURES.Hub,
      authRules: AUTH_RULES.AppLayout
    }
  },
  {
    path: '/board/:boardId',
    name: 'Board',
    component: () => import('@/views/OrgChart.vue'),
    meta: {
      requiresAuth: true,
      title: 'Org Chart',
      feature: FEATURES.OrgChart,
      authRules: AUTH_RULES.AppLayout
    }
  },
  {
    path: '/graph/:boardId',
    name: 'Org Graph',
    component: () => import('@/views/GraphView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Org Chart Graph',
      feature: FEATURES.OrgChart,
      authRules: AUTH_RULES.AppLayout
    }
  },
  {
    path: '/import/:boardId',
    name: 'MainOrgImportData',
    component: () => import('@/views/ImportData.vue'),
    meta: {
      requiresAuth: true,
      title: 'Data Import',
      feature: FEATURES.DataManagement,
      authRules: AUTH_RULES.AppLayout
    }
  },
  {
    path: '/import/:boardId/:planId',
    name: 'PlanImportData',
    component: () => import('@/views/ImportData.vue'),
    meta: {
      requiresAuth: true,
      title: 'Data Import',
      feature: FEATURES.DataManagement,
      authRules: AUTH_RULES.AppLayout
    }
  },
  {
    path: '/import/csv/:boardId',
    name: 'MainOrgImportCSV',
    component: () => import('@/views/ImportCSVFiles.vue'),
    meta: {
      requiresAuth: true,
      title: 'CSV Import',
      feature: FEATURES.DataManagement,
      authRules: AUTH_RULES.AppLayout
    }
  },
  {
    path: '/import/csv/:boardId/:planId',
    name: 'PlanImportCSV',
    component: () => import('@/views/ImportCSVFiles.vue'),
    meta: {
      requiresAuth: true,
      title: 'CSV Import',
      feature: FEATURES.DataManagement,
      authRules: AUTH_RULES.AppLayout
    }
  },
  {
    path: '/formulas/:boardId',
    name: 'Formulas',
    component: () => import('@/views/FormulasView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Formulas',
      feature: FEATURES.DataManagement,
      authRules: AUTH_RULES.AppLayout
    }
  },
  {
    path: '/data-transformation/:boardId',
    name: 'DataTransformation',
    component: () => import('@/views/DataTransformationView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Data Transformation',
      feature: FEATURES.DataManagement,
      authRules: AUTH_RULES.AppLayout
    }
  },
  {
    path: '/table/:boardId',
    name: 'Table',
    component: () => import('@/views/OrgTable.vue'),
    meta: {
      requiresAuth: true,
      title: 'Directory',
      feature: FEATURES.Directory,
      authRules: AUTH_RULES.AppLayout
    }
  },
  {
    path: '/productivity/:boardId',
    name: 'Productivity',
    component: () => import('@/views/KanbanView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Productivity',
      // by removing this we allow navigation to the view where the access is restricted & teaser is shown
      // feature: FEATURES.Productivity,
      authRules: AUTH_RULES.AppLayout
    }
  },
  {
    path: '/integration/:boardId',
    name: 'Main Integration',
    component: () => import('@/views/IntegrationView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Integration',
      feature: FEATURES.DataManagement,
      authRules: AUTH_RULES.AppLayout
    }
  },
  {
    path: '/integration/:boardId/:planId',
    name: 'Plan Integration',
    component: () => import('@/views/IntegrationView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Integration',
      feature: FEATURES.DataManagement,
      authRules: AUTH_RULES.AppLayout
    }
  },
  {
    path: '/plan/:boardId/:planId',
    name: 'Plan View',
    component: () => import('@/views/PlanView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Scenario',
      feature: FEATURES.Scenarios,
      authRules: AUTH_RULES.AppLayout
    }
  },
  {
    path: '/forecast/:boardId',
    name: 'Forecast',
    component: () => import('@/views/ForecastView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Forecast',
      feature: FEATURES.Forecast,
      authRules: AUTH_RULES.AppLayout
    }
  },
  {
    path: '/invite-access/:boardId',
    name: 'InviteAccessUsers',
    component: () => import('@/components/AccessControlDashboard.vue'),
    meta: {
      requiresAuth: true,
      title: 'Access and Invite',
      feature: FEATURES.AccessControl,
      authRules: AUTH_RULES.AppLayout,
      isAdminOnly: true
    }
  },
  {
    path: '/plan/merge/:boardId',
    name: 'PlanMerge',
    component: () => import('@/views/PlanMerge.vue'),
    meta: {
      requiresAuth: true,
      title: 'Merge scenarios',
      feature: FEATURES.Scenarios,
      authRules: AUTH_RULES.AppLayout
    }
  },
  {
    path: '/settings/:boardId',
    name: 'Settings',
    component: () => import('@/views/SettingsView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Settings',
      feature: FEATURES.Settings,
      authRules: AUTH_RULES.AppLayout,
      isAdminOnly: true
    }
  },
  {
    path: '/settings/:boardId/apiKeys',
    name: 'API Keys Settings',
    component: () => import('@/views/ApiKeysView.vue'),
    meta: {
      requiresAuth: true,
      title: 'API Keys Settings',
      feature: FEATURES.Settings,
      authRules: AUTH_RULES.AppLayout,
      isAdminOnly: true
    }
  },
  {
    path: '/pack/:boardId/:planId/:packId',
    name: 'Scenario Slide Pack',
    component: () => import('@/views/SlidePackView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Slide Pack',
      feature: FEATURES.Hub,
      authRules: AUTH_RULES.AppLayout
    }
  },
  {
    path: '/pack/main/:boardId/:planId?',
    name: 'Slide Pack Main',
    component: () => import('@/views/hub/SlidePackLanding.vue'),
    meta: {
      requiresAuth: true,
      title: 'Slide Pack',
      feature: FEATURES.Hub,
      authRules: AUTH_RULES.AppLayout
    }
  },
  {
    path: '/pack/:boardId/:packId',
    name: 'Slide Pack',
    component: () => import('@/views/SlidePackView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Slide Pack',
      feature: FEATURES.Hub,
      authRules: AUTH_RULES.AppLayout
    }
  },
  {
    path: '/oops',
    name: 'No Access',
    component: () => import('@/views/InsufficientAccess.vue'),
    meta: {
      title: 'Access denied',
      layout: AppLayout,
      embedLayout: EmptyLayout,
      authRules: AUTH_RULES.EmbedLayout
    }
  },
  {
    path: '/no-boards',
    name: 'No Boards',
    component: () => import('@/views/NoBoardsAvailable.vue'),
    meta: {
      requiresAuth: true,
      title: 'Agentnoon - no instance available',
      layout: AppLayout,
      authRules: AUTH_RULES.AppLayout
    }
  },
  {
    path: '/invitations',
    name: 'PendingInvitations',
    component: () => import('@/views/PendingInvitations.vue'),
    meta: {
      requiresAuth: true,
      title: 'Pending Invitations',
      layout: AppLayout,
      authRules: AUTH_RULES.AppLayout
    }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'PageNotFound',
    component: () => import('@/views/PageNotFound.vue'),
    meta: {
      layout: AppLayout,
      authRules: AUTH_RULES.AppLayout
    }
  }
]

const router = createRouter({
  history: createWebHistory(baseUrl),
  routes
})

// Redirect embedded entry loads on "/" to the embed handler
router.beforeEach((to, from, next) => {
  const { isEmbedded } = getEmbedStatus()
  const isHomeRoute = to.name === 'Home' || to.path === '/'
  const isAlreadyOnEmbed = to.path === '/embed'

  if (isEmbedded && isHomeRoute && !isAlreadyOnEmbed) {
    const redirectOptions = {
      path: '/embed',
      query: { ...to.query },
      replace: true
    }

    return next(redirectOptions)
  }

  next()
})

// Block untrusted iframe embeddings from rendering any content
router.beforeEach((to, from, next) => {
  const { isEmbedded, isTrusted } = getEmbedTrustStatus()

  if (!isEmbedded) return next()
  if (isTrusted) return next()
  if (to.name === 'No Access') return next()

  logger.warn('[EMBED SECURITY] Untrusted iframe detected, blocking navigation', {
    route: to.name,
    from: from.name,
    parentOrigin: document.referrer
  })

  return next({ name: 'No Access', replace: true })
})

/**
 * Preload features for a board
 * This improves navigation speed for subsequent visits
 */
const preloadFeaturesForBoard = async (boardId) => {
  if (!boardId) return
  // Check if features are already loaded
  const featuresFetched = store.getters['accessScopes/featuresFetched']
  if (featuresFetched !== boardId) {
    store.dispatch('accessScopes/fetchFeatures', { boardId })
  }
}

// Add layouts to all routes that don't have one specified
routes.forEach((route) => {
  if (!route.meta) {
    route.meta = {
      layout: AuthLayout,
      authRules: AUTH_RULES.AuthLayout
    }
  } else if (!route.meta.layout) {
    if (route.meta.requiresAuth === true) {
      // Default authenticated routes to AppLayout
      route.meta.layout = AppLayout
      route.meta.authRules = AUTH_RULES.AppLayout
    } else {
      // Default non-authenticated routes to AuthLayout
      route.meta.layout = AuthLayout
      route.meta.authRules = AUTH_RULES.AuthLayout
    }
  } else {
    // Assign auth rules based on layout if not already defined
    if (!route.meta.authRules) {
      if (route.meta.layout === VerificationLayout) {
        route.meta.authRules = AUTH_RULES.VerificationLayout
      } else if (route.meta.layout === AppLayout) {
        route.meta.authRules = AUTH_RULES.AppLayout
      } else if (route.meta.layout === AuthLayout) {
        route.meta.authRules = AUTH_RULES.AuthLayout
      }
    }
  }
})

// Set up authentication global navigation guard
router.beforeEach(async (to, from, next) => {
  // Start route span using Faro's context - this is the key change
  routeSpanManager.startRouteSpan(to)

  // Wrap the entire route guard logic in the route context
  return routeSpanManager.withRouteContext(async () => {
    // Initialize auth service if not already done
    if (!store.getters.isInitialized) {
      try {
        await store.dispatch('initialize')
      } catch (error) {
        logger.warn('Auth initialization error, proceeding with best effort', error.message)
      }
    }

    // Wait for auth service to be initialized
    try {
      await until(() => store.getters.isInitialized).toBeTruthy({ timeout: 1000 })
    } catch (error) {
      logger.warn('Auth initialization timeout, proceeding with best effort')
    }

    // Check for routes that require specific query parameters
    if (to.meta.requiresOobCode && !to.query.oobCode) {
      return next({ name: 'Home' })
    }

    // Get auth rules directly from route metadata
    const authRules = to.meta.authRules

    if (!authRules) {
      return next()
    }

    // Get current auth state (use sync version for performance in route guard)
    const currentAuthState = await store.dispatch('getCurrentAuthState')

    // Helper function to prevent redirect loops
    const wouldCauseRedirectLoop = (targetRouteName) => {
      return to.name === targetRouteName
    }

    // Check if the current auth state is allowed for this layout
    const isAuthStateAllowed = authRules.allowedAuthStates.includes(currentAuthState)

    // If not allowed, determine where to redirect
    if (!isAuthStateAllowed) {
      const redirectTarget = authRules.redirects[currentAuthState]

      // Prevent redirect loops
      if (wouldCauseRedirectLoop(redirectTarget)) {
        return next()
      }

      // Add query param for redirect after login if unauthenticated
      const redirectOptions = {
        name: redirectTarget
      }

      // Save the original destination if redirecting to login
      if (currentAuthState === AUTH_STATE.UNAUTHENTICATED) {
        redirectOptions.query = { redirect: to.fullPath }
      }

      return next(redirectOptions)
    }

    // Check feature access
    if (currentAuthState === AUTH_STATE.VERIFIED && to.meta.feature && to.params.boardId) {
      try {
        const hasAccess = await featureAccessService.checkFeatureAccess({
          boardId: to.params.boardId,
          feature: to.meta.feature
        })

        if (!hasAccess) {
          return next({ name: 'No Access' })
        }
      } catch (error) {
        logger.error('Feature access check failed:', error.message)
        return next({ name: 'No Access' })
      }
    }

    // Check admin access
    if (currentAuthState === AUTH_STATE.VERIFIED && to.meta.isAdminOnly && to.params.boardId) {
      try {
        const isAdmin = store.getters.isAdmin(to.params.boardId)

        if (!isAdmin) {
          return next({ name: 'No Access' })
        }
      } catch (error) {
        logger.error('Admin access check failed:', error.message)
        return next({ name: 'No Access' })
      }
    }

    next()
  })
})

// Special guard for Home to Landing redirection
router.beforeEach((to, from, next) => {
  // If user is at home page but is already authenticated, redirect to Landing
  if (to.name === 'Home' && store.getters.isLoggedIn && store.getters.isVerified) {
    return next({ name: 'Landing' })
  }
  next()
})

// Set up board navigation guard (runs after auth guard)
router.beforeEach(async (to, from, next) => {
  try {
    // Only proceed with board validation if auth has passed
    // This ensures we don't try to validate boards for unauthenticated users
    const { isEmbedded } = getEmbedStatus()

    // If the user is explicitly set to no_access for this board, route them to a dedicated access-denied UI.
    if (store.getters.isLoggedIn && to.params.boardId) {
      let role = store.getters.role(to.params.boardId)
      if (!role) {
        await boardNavigationService.ensureBoardsLoaded()
        role = store.getters.role(to.params.boardId)
      }

      if (role === ACCESS_LEVELS_DICT.NO_ACCESS) {
        return next({ name: 'No Access' })
      }
    }

    // Embedded users bypass emailVerified, so this check runs outside the
    // isVerified gate. Resolves sync/onboarding state before Landing renders.
    if (isEmbedded && store.getters.isLoggedIn && to.name === 'Landing' && to.params.boardId) {
      const targetBoardId = to.params.boardId

      const { isInProgress } = await checkSyncStatus(targetBoardId)
      if (isInProgress) {
        logger.info(
          '[Router Guard] REDIRECT → EmbedNoBoards (sync in progress for',
          targetBoardId,
          ')'
        )
        saveSyncBoardId(targetBoardId)
        return next({ name: 'EmbedNoBoards' })
      }

      let isOnboardingComplete = store.getters.dayforceOnboardingCompleted
      if (!isOnboardingComplete) {
        await store.dispatch('fetchSettings', { boardId: targetBoardId })
        isOnboardingComplete = store.getters.dayforceOnboardingCompleted
      }
      if (!isOnboardingComplete) {
        logger.info('[Router Guard] REDIRECT → DayforceOnboarding (onboarding not complete)')
        return next({
          name: 'DayforceOnboarding',
          params: { boardId: targetBoardId }
        })
      }
    }

    if (store.getters.isLoggedIn && (store.getters.isVerified || isEmbedded)) {
      // Prevent navigating away from EmbedNoBoards while Dayforce sync is in progress
      if (from.name === 'EmbedNoBoards' && to.name !== 'EmbedNoBoards') {
        logger.info('[Router Guard] Leaving EmbedNoBoards, target:', to.name)
        const mainBoards = store.getters.mainBoards
        logger.info('[Router Guard] Checking sync status for', mainBoards?.length || 0, 'boards')
        if (mainBoards && mainBoards.length > 0) {
          for (const board of mainBoards) {
            const { isInProgress } = await checkSyncStatus(board.boardId)
            logger.info('[Router Guard] Board', board.boardId, 'isInProgress:', isInProgress)
            if (isInProgress) {
              logger.info('[Router Guard] BLOCKED - sync in progress, staying on EmbedNoBoards')
              return next(false)
            }
          }
        }
        logger.info('[Router Guard] ALLOWED - no sync in progress')
      }

      // Prevent navigating away from DayforceOnboarding until onboarding is complete (embedded users only)
      if (from.name === 'DayforceOnboarding' && to.name !== 'DayforceOnboarding') {
        logger.info(
          '[Router Guard] Leaving DayforceOnboarding, target:',
          to.name,
          'isEmbedded:',
          isEmbedded
        )
        if (isEmbedded) {
          const boardId = from.params.boardId
          const isOnboardingComplete = store.getters.dayforceOnboardingCompleted
          logger.info(
            '[Router Guard] boardId:',
            boardId,
            'isOnboardingComplete:',
            isOnboardingComplete
          )

          if (boardId && !isOnboardingComplete) {
            logger.info(
              '[Router Guard] BLOCKED - onboarding not complete, staying on DayforceOnboarding'
            )
            return next(false)
          }
          logger.info('[Router Guard] ALLOWED - onboarding complete or skipped')
        }
      }

      // Prevent authenticated users with boards from accessing the No Boards routes
      if (to.name === 'No Boards' || to.name === 'EmbedNoBoards') {
        logger.info('[Router Guard] Entering No Boards route:', to.name, 'isEmbedded:', isEmbedded)
        await boardNavigationService.ensureBoardsLoaded()

        // Get the updated list of boards
        const availableBoards = store.getters.mainBoards
        logger.info('[Router Guard] Available boards:', availableBoards?.length || 0)

        if (availableBoards && availableBoards.length > 0) {
          // If there's an active Dayforce sync in progress on any board, stay on EmbedNoBoards
          // to let the user see the sync status and resume polling
          if (to.name === 'EmbedNoBoards') {
            logger.info('[Router Guard] Checking sync status for EmbedNoBoards access')
            for (const board of availableBoards) {
              const { isInProgress } = await checkSyncStatus(board.boardId)
              logger.info('[Router Guard] Board', board.boardId, 'isInProgress:', isInProgress)
              if (isInProgress) {
                logger.info('[Router Guard] ALLOWED - sync in progress, staying on EmbedNoBoards')
                saveSyncBoardId(board.boardId)
                return next()
              }
            }
            logger.info('[Router Guard] No sync in progress, will redirect')
            clearSyncBoardId()
          }
          // Ensure invitations are loaded before determining default board
          await boardNavigationService.ensureInvitationsLoaded()

          const defaultBoardId = boardNavigationService.getDefaultBoardId()
          const targetBoardId = defaultBoardId || availableBoards[0].boardId
          logger.info('[Router Guard] targetBoardId:', targetBoardId)

          // Check if Dayforce onboarding is needed (only for embedded users)
          if (isEmbedded && targetBoardId) {
            // If data sync is still running, stay on EmbedNoBoards so the
            // user can see progress — don't jump ahead to onboarding.
            const { isInProgress: syncRunning } = await checkSyncStatus(targetBoardId)
            if (syncRunning) {
              logger.info(
                '[Router Guard] REDIRECT → EmbedNoBoards (sync in progress for',
                targetBoardId,
                ')'
              )
              saveSyncBoardId(targetBoardId)
              return next({ name: 'EmbedNoBoards' })
            }

            logger.info('[Router Guard] Fetching settings for board-scoped onboarding check')
            await store.dispatch('fetchSettings', { boardId: targetBoardId })
            const isOnboardingComplete = store.getters.dayforceOnboardingCompleted
            logger.info(
              '[Router Guard] Onboarding check - isOnboardingComplete (after fetch):',
              isOnboardingComplete
            )

            if (!isOnboardingComplete) {
              logger.info('[Router Guard] REDIRECT → DayforceOnboarding (onboarding not complete)')
              return next({
                name: 'DayforceOnboarding',
                params: { boardId: targetBoardId }
              })
            }
          }

          logger.info('[Router Guard] REDIRECT → Landing')
          return next({
            name: 'Landing',
            params: { boardId: targetBoardId }
          })
        }
        logger.info('[Router Guard] No boards, allowing access to', to.name)
      }

      // Special case: direct navigation to Landing without boardId
      if (to.name === 'Landing' && !to.params.boardId) {
        // Ensure invitations are loaded before determining default board
        await boardNavigationService.ensureInvitationsLoaded()
        const defaultBoardId = boardNavigationService.getDefaultBoardId()

        // Agentnoon internal: send verified users to Super Admin when landing without boardId
        if (
          store.getters.myEmail?.endsWith?.('@agentnoon.com') &&
          !boardNavigationService.getDefaultBoardIdForSuperAdmin() &&
          import.meta.env.MODE === 'production'
        ) {
          return next({ name: 'Super Admin' })
        }
        if (defaultBoardId) {
          return next({
            name: 'Landing',
            params: { boardId: defaultBoardId },
            query: to.query
          })
        }
      }

      const redirectRoute = await boardNavigationService.validateRouteNavigation(to)

      // If redirect is needed, go there
      if (redirectRoute) {
        return next(redirectRoute)
      }

      // Track board transitions (only after successful validation)
      if (from.params.boardId !== to.params.boardId && from.params.boardId && to.params.boardId) {
        boardNavigationService.trackBoardTransition(from.params.boardId, to.params.boardId)
      }
    }

    next()
  } catch (error) {
    logger.error('Error in board navigation guard:', error.message)
    // Continue despite errors to avoid blocking navigation
    next()
  }
})

// Add a simple route middleware to preload features when navigating to a board
router.beforeResolve((to, from, next) => {
  // Preload features for this board to speed up future navigations
  if (to.params.boardId) {
    // Non-blocking preload
    preloadFeaturesForBoard(to.params.boardId).catch((error) => {
      logger.warn('Failed to preload features:', error.message)
    })
  }
  next()
})

router.onError((error, to) => {
  if (
    error.message.includes('Failed to fetch dynamically imported module') ||
    error.message.includes('Importing a module script failed')
  ) {
    window.location = to.fullPath
  }
})

// Embedded session guard: only allow admin-only routes if the iframe launch URL
// explicitly contained a supported destination (e.g., settings/access-control).
// This prevents post-load query tampering from revealing admin surfaces.
router.beforeEach((to, from, next) => {
  // Check embed trust dynamically at navigation time (not at module load)
  const { isEmbedded, isTrusted } = getEmbedTrustStatus()

  if (!isEmbedded) return next()
  if (!to.meta.isAdminOnly) return next()

  // Read destination from sessionStorage dynamically at navigation time
  // This ensures we check against the current embed session, not stale initialization data
  const currentDestination = sessionStorage.getItem('embed-destination')
  const currentAllowedRoutes = EMBED_ALLOWED_DESTINATIONS[currentDestination] || []

  logger.info('[EMBED SECURITY] Checking admin route access:', {
    route: to.name,
    destination: currentDestination,
    isTrusted,
    allowedRoutes: currentAllowedRoutes
  })

  const isAllowedRoute = currentAllowedRoutes.length > 0 && currentAllowedRoutes.includes(to.name)

  if (!isTrusted || !isAllowedRoute) {
    logger.warn('[EMBED SECURITY] Access denied:', {
      route: to.name,
      destination: currentDestination,
      isTrusted,
      isAllowedRoute
    })
    return next({ name: 'No Access' })
  }

  logger.info('[EMBED SECURITY] Access granted to:', to.name)
  return next()
})

// Route tracking and title updates
router.afterEach((to) => {
  if (window.mixpanel) {
    window.mixpanel.track(`route_${to.name}`)
  }

  // End the route span after a delay to catch late XHR requests
  routeSpanManager.endRouteSpan()

  const { isEmbedded } = getEmbedStatus()
  const defaultTitle = isEmbedded ? 'Dayforce SWP' : 'Agentnoon'
  const metaTitle = to.meta?.title
  const computedTitle =
    isEmbedded && metaTitle?.includes?.('Agentnoon')
      ? metaTitle.replace(/Agentnoon/gi, 'Dayforce SWP')
      : metaTitle || defaultTitle

  document.title = computedTitle
})

export default router
